<template>
  <PycadViewerOverlay
    :label="label"
    :label-color="labelColor"
    :top-orientation="topLabel"
    :left-orientation="leftLabel"
    :patient-name="patientName"
    :patient-meta="patientMeta"
    :study-date="studyDate"
    :slice-info="sliceInfoDisplay"
    :window-level="windowLevelDisplay"
    :show-slice="!!sliceConfig"
    :show-window-level="!!wlConfig"
    :bottom-pad="showSliderClearance ? '40px' : '12px'"
  >
    <template #top-right>
      <slot name="top-right" />
    </template>
    <template #bottom-right>
      <slot name="bottom-right" />
    </template>
    <template #bottom-left>
      <slot name="bottom-left" />
    </template>
  </PycadViewerOverlay>
</template>

<script setup lang="ts">
import { computed, inject, toRefs } from 'vue';
import { Maybe } from '@/src/types';
import { useSliceConfig } from '@/src/composables/useSliceConfig';
import { useWindowingConfig } from '@/src/composables/useWindowingConfig';
import { useOrientationLabels } from '@/src/composables/useOrientationLabels';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useImage } from '@/src/composables/useCurrentImage';
import { useViewStore } from '@/src/store/views';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { isDicomImage } from '@/src/utils/dataSelection';
import {
  getPatientMeta,
  getPatientName,
  getStudyDate,
} from '@/src/utils/dicomDisplay';
import { useAppConfigStore } from '@/src/store/app-config';
import PycadViewerOverlay from './PycadViewerOverlay.vue';

const props = withDefaults(
  defineProps<{
    viewId: string;
    imageId: Maybe<string>;
    /** Add padding so corner HUD doesn't overlap the bottom MPR slice strip. */
    showSliderClearance?: boolean;
  }>(),
  {
    showSliderClearance: true,
  }
);

const { viewId, imageId } = toRefs(props);

const view = inject(VtkViewContext);
if (!view)
  throw new Error('PycadSliceViewerOverlay must live inside a VtkView');

const { top: topLabel, left: leftLabel } = useOrientationLabels(view);

const {
  config: sliceConfig,
  slice,
  range: sliceRange,
} = useSliceConfig(viewId, imageId);

const {
  config: wlConfig,
  width: windowWidth,
  level: windowLevel,
} = useWindowingConfig(viewId, imageId);

const { metadata } = useImage(imageId);

// Determine a stable plane label + color (Axial / Sagittal / Coronal / Oblique).
const viewStore = useViewStore();
const viewName = computed(() => viewStore.getView(viewId.value)?.name ?? '');

const PLANE_META: Record<string, { label: string; color: string }> = {
  Axial: { label: 'AXIAL', color: '#3B9EFF' },
  Sagittal: { label: 'SAGITTAL', color: '#FF5C5C' },
  Coronal: { label: 'CORONAL', color: '#4ADE80' },
  ObliqueAxial: { label: 'OBL AXIAL', color: '#3B9EFF' },
  ObliqueSagittal: { label: 'OBL SAGITTAL', color: '#FF5C5C' },
  ObliqueCoronal: { label: 'OBL CORONAL', color: '#4ADE80' },
  Oblique: { label: 'OBLIQUE', color: '#ff9900' },
};

const planeMeta = computed(() => {
  const direct = PLANE_META[viewName.value];
  if (direct) return direct;
  const idLower = viewId.value.toLowerCase();
  if (idLower.includes('axial')) return PLANE_META.Axial;
  if (idLower.includes('sagittal')) return PLANE_META.Sagittal;
  if (idLower.includes('coronal')) return PLANE_META.Coronal;
  const appConfig = useAppConfigStore();
  const fallbackName = appConfig.anonymize
    ? viewName.value || 'VIEW'
    : metadata.value?.name || viewName.value || 'VIEW';
  return {
    label: fallbackName.toUpperCase(),
    color: '#ff9900',
  };
});

const label = computed(() => planeMeta.value.label);
const labelColor = computed(() => planeMeta.value.color);

const sliceInfoDisplay = computed(() => {
  if (!sliceConfig.value) return undefined;
  return {
    current: slice.value + 1,
    total: (sliceRange.value[1] ?? 0) + 1,
  };
});

const windowLevelDisplay = computed(() => {
  if (!wlConfig.value) return undefined;
  return {
    width: windowWidth.value.toFixed(0),
    level: windowLevel.value.toFixed(0),
  };
});

// --- Patient metadata --- //
const dicomStore = useDICOMStore();
const patientName = computed(() => {
  const id = imageId.value;
  if (!id || !isDicomImage(id)) return '';
  const studyKey = dicomStore.volumeStudy[id];
  const patientKey = studyKey ? dicomStore.studyPatient[studyKey] : undefined;
  const info = patientKey ? dicomStore.patientInfo[patientKey] : undefined;
  return getPatientName(info);
});
const patientMeta = computed(() => {
  const id = imageId.value;
  if (!id || !isDicomImage(id)) return '';
  const studyKey = dicomStore.volumeStudy[id];
  const patientKey = studyKey ? dicomStore.studyPatient[studyKey] : undefined;
  const info = patientKey ? dicomStore.patientInfo[patientKey] : undefined;
  return getPatientMeta(info);
});
const studyDate = computed(() => {
  const id = imageId.value;
  if (!id || !isDicomImage(id)) return '';
  const studyKey = dicomStore.volumeStudy[id];
  const info = studyKey ? dicomStore.studyInfo[studyKey] : undefined;
  return getStudyDate(info);
});
</script>
